import React, { useState, useEffect } from "react";
import {
  DatePicker,
  InputNumber,
  Switch,
  Input,
  Button,
  message,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import GuideListingService from "../services/GuideListingService";
import { getCoinBalance } from "../services/coinService";
import NavUser from "../components/NavUser";
import "./GuideBooking.css";

const { TextArea } = Input;

const GuideBookingSummeryScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [tourData, setTourData] = useState({});
  const [guideData, setGuideData] = useState({});
  const [bookingDate, setBookingDate] = useState(null);
  const [numberOfGuests, setNumberOfGuests] = useState(1);
  const [availableGuests, setAvailableGuests] = useState(10);
  const [isFreePickup, setIsFreePickup] = useState(false);
  const [specialRequests, setSpecialRequests] = useState("");
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [coinBalance, setCoinBalance] = useState(0);
  const [coinReward, setCoinReward] = useState(0);

  useEffect(() => {
    if (location.state && location.state.tour && location.state.guide) {
      setTourData(location.state.tour);
      setGuideData(location.state.guide);
      setCoinReward(parseInt(location.state.tour.coin_reward) || 0);
    } else {
      message.error("Tour or Guide data not found. Please go back and select a tour.");
      navigate("/tours");
    }

    const fetchCoinBalance = async () => {
      const userData = JSON.parse(localStorage.getItem("user"));
      if (userData && userData.id) {
        const balance = await getCoinBalance(userData.id);
        setCoinBalance(balance);
      }
    };
    fetchCoinBalance();
  }, [location.state, navigate]);

  const fetchAvailableGuests = async (date) => {
    if (!date || !tourData.id) return;

    setLoadingGuests(true);
    try {
      const formattedDate = date.format("YYYY-MM-DD");
      const data = await GuideListingService.getAvailableGuests(
        tourData.id,
        formattedDate
      );
      const fetchedAvailableGuests = data["available_guests"] ?? 0;

      setAvailableGuests(fetchedAvailableGuests);
      if (fetchedAvailableGuests > 0) {
        setNumberOfGuests(1);
      } else {
        setNumberOfGuests(0);
      }
    } catch (error) {
      console.error("Failed to get available guests:", error);
      message.error(`Failed to load available guest count: ${error.message}`);
      setAvailableGuests(0);
      setNumberOfGuests(0);
    } finally {
      setLoadingGuests(false);
    }
  };

  const onDateChange = (date) => {
    setBookingDate(date);
    if (date) {
      fetchAvailableGuests(date);
    } else {
      setAvailableGuests(10);
      setNumberOfGuests(1);
    }
  };

  const handleSubmit = () => {
    if (!bookingDate) {
      message.error("Please select a booking date before proceeding.");
      return;
    }

    if (numberOfGuests === 0) {
      message.error("No slots available for the selected date.");
      return;
    }

    const bookingDetails = {
      bookingDate: bookingDate.format("YYYY-MM-DD"),
      numberOfGuests: numberOfGuests,
      pickupRequested: isFreePickup,
      specialRequests: specialRequests,
      tourListingId: tourData.id,
      applyRewards: false,
    };

    navigate("/reviewguidebook", {
      state: {
        tour: tourData,
        guide: guideData,
        bookingDetails: bookingDetails,
      },
    });
  };

  return (
    <div className="guide-booking-container">
      <NavUser />
      
      <div className="guide-booking-content">
        <div className="booking-header">
          <h1>Booking Summary</h1>
          <p>Review and finalize your tour details</p>
        </div>

        {/* Coin Balance */}
        <div className="premium-card coin-balance-card">
          <span className="coin-label">Your Coin Balance</span>
          <span className="coin-value">🪙 {coinBalance} <small style={{fontSize: '12px', marginLeft: '4px', opacity: 0.7}}>COINS</small></span>
        </div>

        {/* Tour Info */}
        <div className="premium-card">
          <div className="tour-info-grid">
            <div>
              <h2 className="tour-title">{tourData.title || "Tour Title"}</h2>
              <p style={{ color: '#718096', margin: '4px 0 0 0' }}>Professional Guided Experience</p>
            </div>
            <div className="tour-price">${tourData.price || "N/A"}</div>
          </div>
          {coinReward > 0 && (
            <div style={{ 
              marginTop: '16px', 
              padding: '12px', 
              background: '#f0fff4', 
              borderRadius: '12px', 
              color: '#38a169', 
              fontWeight: '600',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              ✨ Earn {coinReward} coins with this booking!
            </div>
          )}
        </div>

        {/* Discounts */}
        <div className="premium-card">
          <h3 className="section-title">Discounts & Offers</h3>
          <div className="discount-item">
            <span className="discount-label">Tour Discount</span>
            <span className="discount-value">{parseFloat(tourData.discount || "0").toFixed(0)}% OFF</span>
          </div>
          <div style={{ marginTop: '16px' }}>
            <p style={{ fontSize: '13px', fontWeight: '700', color: '#4a5568', textTransform: 'uppercase', marginBottom: '8px' }}>Group Pricing</p>
            {tourData.group_pricings?.length > 0 ? (
              tourData.group_pricings.map((pricing, index) => (
                <div key={index} className="discount-item" style={{ padding: '4px 0', border: 'none' }}>
                  <span style={{ fontSize: '14px', color: '#718096' }}>{pricing.member_count}+ people</span>
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#2d3748' }}>{pricing.discount}% OFF</span>
                </div>
              ))
            ) : (
              <p style={{ color: "#a0aec0", fontSize: '14px', fontStyle: 'italic' }}>No group discounts available at this time.</p>
            )}
          </div>
        </div>

        {/* Booking Details Form */}
        <div className="premium-card">
          <h3 className="section-title">Booking Details</h3>
          
          <div className="form-group">
            <label>Select Booking Date</label>
            <DatePicker
              onChange={onDateChange}
              value={bookingDate}
              format="YYYY-MM-DD"
              disabledDate={(current) => current && current < moment().startOf("day")}
              className="custom-input"
              size="large"
            />
            {bookingDate && availableGuests === 0 && !loadingGuests && (
              <p style={{ color: "#e53e3e", fontSize: '12px', marginTop: '8px', fontWeight: '500' }}>
                ⚠️ No slots available for this date.
              </p>
            )}
          </div>

          <div className="form-group">
            <label>Number of Guests</label>
            <InputNumber
              min={1}
              max={availableGuests}
              value={numberOfGuests}
              onChange={(value) => setNumberOfGuests(value || 1)}
              disabled={availableGuests === 0 || loadingGuests}
              className="custom-input"
              size="large"
            />
            <p style={{ color: "#718096", fontSize: '12px', marginTop: '6px' }}>
              Available capacity: {loadingGuests ? "Checking..." : availableGuests} guests
            </p>
          </div>

          <div className="pickup-toggle">
            <div>
              <p style={{ fontWeight: '600', margin: 0, color: '#2d3748' }}>Free Pickup Service</p>
              <p style={{ fontSize: '12px', color: '#718096', margin: 0 }}>Complimentary pickup from your location</p>
            </div>
            <Switch checked={isFreePickup} onChange={setIsFreePickup} />
          </div>

          <div className="form-group" style={{ marginTop: '24px' }}>
            <label>Special Requests</label>
            <TextArea
              rows={4}
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              placeholder="Any dietary requirements, accessibility needs, or special requests?"
              className="custom-input"
            />
          </div>

          <div className="promo-tile" onClick={() => message.info("Promo code functionality coming soon!")}>
            <span style={{ fontWeight: '500', color: '#4a5568' }}>🎟️ Have a promo code?</span>
            <span style={{ color: '#3182ce', fontWeight: '700' }}>Apply Now &gt;</span>
          </div>
        </div>

        <Button
          type="primary"
          className="review-btn"
          onClick={handleSubmit}
          disabled={availableGuests === 0 || loadingGuests}
        >
          {loadingGuests ? "Checking Availability..." : "Review & Confirm Booking"}
        </Button>
      </div>
    </div>
  );
};

export default GuideBookingSummeryScreen;

